import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';

class ParallaxContext extends PureComponent {
  static propTypes = {
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.array]),
  };

  static childContextTypes = {
    registerElement: PropTypes.func,
    unregisterElement: PropTypes.func,
  };

  getChildContext = () => {
    return {
      registerElement: this.registerElement,
      unregisterElement: this.unregisterElement,
    };
  };

  nextID = 0;
  rafID = null;
  prevScrollPosition = null;
  scrollPosition = null;
  windowHeight = null;
  elements = [];
  mustInvalidate = false;

  throttledResize = null;

  constructor(props) {
    super(props);

    this.loop = this.loop.bind(this);
  }

  isInView = element => {
    if (this.scrollPosition === null) return false;

    const top = element.attributes.rect.top; // + element.attributes.transform.y;
    const bottom = top + element.attributes.rect.height;

    const topInView =
      top >= this.scrollPosition &&
      top <= this.scrollPosition + this.windowHeight;
    const bottomInView =
      bottom >= this.scrollPosition &&
      bottom <= this.scrollPosition + this.windowHeight;
    return topInView || bottomInView;
  };

  checkInView = element => {
    const isInView = this.isInView(element);

    if (element.inViewCallback) {
      element.inViewCallback(isInView);
      if (isInView && element.inViewOnce) {
        element.inViewCallback = null;
        this.unregisterElement(element.id);
      }
    }
    return isInView;
  };

  registerElement = (
    el,
    nestedEl,
    depth,
    topAlign = false,
    inViewCallback = null,
    inViewOnce = true
  ) => {
    const id = this.nextID++;
    const element = {
      id,
      el,
      nestedEl,
      depth,
      topAlign,
      inViewCallback,
      inViewOnce,
    };

    this.elements = [...this.elements, element];
    this.calculateElementAttributes(element);
    this.checkInView(element);

    return id;
  };

  unregisterElement = elementID => {
    const updatedElements = this.elements.filter(el => el.id !== elementID);
    this.elements = updatedElements;
  };

  updateElements = () => {
    this.elements.forEach(element => {
      this.calculateElementAttributes(element);
      this.calculateElementTransform(element);
    });
  };

  calculateElementAttributes = element => {
    const rect = element.el.getBoundingClientRect();
    const top = rect.top + this.scrollPosition;
    const bottom = top + rect.height;

    element.attributes = {
      rect: {
        width: rect.width,
        height: rect.height,
        top,
        bottom,
      },
      transform: {
        y: 0,
      },
    };
  };

  calculateElementTransform = element => {
    const { depth, topAlign, attributes: { rect: { top, height } } } = element;
    if (depth === 0) return;

    const speed = Math.abs(depth) > 0 ? 1.0 / depth : 0;

    const yOffset =
      top +
      (topAlign ? 0 : height * 0.5) -
      (this.scrollPosition + (topAlign ? 0 : this.windowHeight * 0.5));

    const y = (element.attributes.transform.y =
      yOffset / (this.windowHeight * 0.5) * speed * this.windowHeight);

    //element.nestedEl.style.cssText = `transform: translateY(${y}px);`;
    element.nestedEl.style.cssText = `transform: translate3d(0, ${y}px, 0);`;
  };

  //componentWillReceiveProps = () => {
  componentDidUpdate = () => {
    requestAnimationFrame(() => {
      this.updateElements();
    });
  };

  componentDidMount = () => {
    this.throttledResize = throttle(this.onResize, 50);
    window.addEventListener('resize', this.throttledResize);
    window.addEventListener('scroll', this.onScroll, false);

    requestAnimationFrame(this.loop);
    document.addEventListener('DOMContentLoaded', this.onLoad);
  };

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.throttledResize);
    window.removeEventListener('scroll', this.onScroll, false);

    cancelAnimationFrame(this.rafID);
    document.removeEventListener('DOMContentLoaded', this.onLoad);
  };

  onLoad = event => {
    this.onResize();
    document.removeEventListener('DOMContentLoaded', this.onLoad);
  };

  onResize = event => {
    this.windowHeight = window.innerHeight;
    this.updateElements();
  };

  onScroll = event => {
    this.mustInvalidate = true;
    this.scrollPosition = window.pageYOffset;
    //window.removeEventListener('scroll', this.onScroll, false);
  };

  loop() {
    //const prevScrollPosition = this.scrollPosition;
    //this.scrollPosition = window.pageYOffset;
    if (this.prevScrollPosition === null || this.mustInvalidate) {
      if (this.prevScrollPosition === null) {
        this.scrollPosition = window.pageYOffset;
      }
      this.updateElements();
      this.mustInvalidate = false;
    }

    if (this.scrollPosition !== this.prevScrollPosition) {
      this.elements.forEach(element => {
        const isInView = this.checkInView(element);
        if (!isInView) return;

        this.calculateElementTransform(element);
      });
    }
    this.prevScrollPosition = this.scrollPosition;
    requestAnimationFrame(this.loop);
  }

  render() {
    return this.props.children;
  }
}

const mapStateToProps = state => {
  const { content: { updated: { timestamp } } } = state.entities;
  const { location: { pathname } } = state.router;

  return {
    timestamp,
    pathname,
  };
};

export default connect(mapStateToProps, null)(ParallaxContext);



// WEBPACK FOOTER //
// ./src/modules/parallax/components/ParallaxContext.js